import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { DPN_BASE } from './defaultPricingNew';

/** One TAT as the popup lists it (G5 TurnAroundTime_ListView_TestPriceSurcharge). */
interface TatOption { tat: string; hours: number }

// G5 columns: TAT · Hours (the TurnAroundTime Count), sorted by Hours.
const COLUMNS: AppColumn<TatOption>[] = [
  { field: 'tat', header: 'TAT', width: 320 },
  { field: 'hours', header: 'Hours', align: 'right', text: (r) => r.hours.toLocaleString('en-US') },
];

/**
 * Default Pricing ▸ New — the G5 "Turn Around Time" popup, opened by double-clicking
 * a row's TAT cell: Search · TAT / Hours grid with tick boxes · OK / Cancel. The
 * row's current TATs are ticked when it opens; OK hands back the ticked names in
 * the list's order (fastest first), which is how G5 joins them.
 */
export function TatPickerDialog({ picked, onClose, onApply }: {
  /** TAT names already on the row. */
  picked: string[];
  onClose: () => void;
  onApply: (tats: string[]) => void;
}) {
  const notify = useNotify();
  const [rows, setRows] = useState<TatOption[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set(picked));
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    api.get<{ data?: TatOption[] }>(`${DPN_BASE}/tat-picker`)
      .then(({ data }) => { if (alive) setRows(data.data ?? []); })
      .catch(() => { if (alive) notify.error('The turnaround times could not be loaded.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [notify]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => r.tat.toLowerCase().includes(q) || String(r.hours).includes(q)) : rows;
  }, [rows, search]);

  const ok = () => onApply(rows.filter((r) => selected.has(r.tat)).map((r) => r.tat));

  return (
    <AppDialog
      open
      title="Turn Around Time"
      width={720}
      className="dlg-center"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={ok}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="clp-pick-top">
        <span className="clp-pick-count">{selected.size} selected</span>
        {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
      </div>
      <AppDataGrid<TatOption>
        rows={visible}
        columns={COLUMNS}
        getRowId={(r) => r.tat}
        loading={loading}
        defaultPageSize={20}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        emptyText="No turnaround times are defined."
      />
    </AppDialog>
  );
}
